import { useEffect, useMemo, useState } from "react";
import { api, euro } from "@/lib/api";
import { InternalLayout, PageHeader } from "@/components/InternalLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MultiSelect } from "@/components/MultiSelect";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, PieChart, Pie, Cell } from "recharts";
import { DownloadSimpleIcon as DownloadSimple, CurrencyEurIcon as CurrencyEur, ArrowCounterClockwiseIcon as ArrowCounterClockwise, MagnifyingGlassIcon as MagnifyingGlass, StackIcon as Stack, TrendUpIcon as TrendUp } from "@phosphor-icons/react";

const COLORS = ["#00396a", "#0b8457", "#d97706", "#7c3aed", "#dc2626", "#0891b2", "#db2777", "#65a30d", "#ea580c", "#2563eb", "#059669", "#9333ea", "#0d9488", "#c026d3"];
const tipStyle = { contentStyle: { background: "#09090B", border: "none", borderRadius: 4, color: "#fff", fontSize: 12 } };
const num = (v) => Number(v) || 0;
const pct = (v) => `${(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;

const displayCli = (s) => s.cliente_nome || s.zyxel_org_name || "—";
const isAssigned = (s) => !!s.cliente_nome;
const daysTo = (d) => (d ? Math.floor((new Date(d) - new Date()) / 86400000) : null);

const EMPTY_F = { q: "", fornitori: [], tipi: [], fatturazioni: [], scadenza: "all", assegnazione: "all", margine: "all" };

function Kpi({ label, value, accent, icon: Icon, sub }) {
  return (
    <Card className="rounded-sm border-border shadow-none p-5" data-testid={`svc-kpi-${label}`}>
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1">
        {Icon && <Icon size={13} />} {label}
      </div>
      <div className={`font-heading text-2xl font-bold mt-2 tabular-nums ${accent || ""}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </Card>
  );
}

export default function ServiziReport() {
  const [services, setServices] = useState(null);
  const [f, setF] = useState(EMPTY_F);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => { api.get("/services").then(({ data }) => setServices(data)).catch(() => setServices([])); }, []);

  const opts = useMemo(() => {
    const src = services || [];
    const uniq = (key) => [...new Set(src.map((s) => s[key]).filter(Boolean))].sort();
    return {
      fornitori: uniq("fornitore").map((v) => ({ value: v, label: v })),
      tipi: uniq("tipo").map((v) => ({ value: v, label: v })),
      fatturazioni: uniq("fatturazione_ricorrente").map((v) => ({ value: v, label: v })),
    };
  }, [services]);

  const filtered = useMemo(() => {
    if (!services) return [];
    const q = f.q.trim().toLowerCase();
    return services.filter((s) => {
      if (q) {
        const hay = `${s.nome || ""} ${displayCli(s)} ${s.fornitore || ""} ${s.tipo || ""} ${s.riferimento_esterno || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (f.fornitori.length && !f.fornitori.includes(s.fornitore)) return false;
      if (f.tipi.length && !f.tipi.includes(s.tipo)) return false;
      if (f.fatturazioni.length && !f.fatturazioni.includes(s.fatturazione_ricorrente)) return false;
      if (f.assegnazione === "assegnati" && !isAssigned(s)) return false;
      if (f.assegnazione === "non_assegnati" && isAssigned(s)) return false;
      const m = num(s.prezzo_vendita) - num(s.prezzo_acquisto);
      if (f.margine === "positivo" && !(m > 0)) return false;
      if (f.margine === "negativo" && !(m < 0)) return false;
      if (f.margine === "zero" && m !== 0) return false;
      if (f.scadenza !== "all") {
        const d = daysTo(s.data_scadenza);
        if (f.scadenza === "scaduti" && !(d != null && d < 0)) return false;
        if (f.scadenza === "attivi" && !(d == null || d >= 0)) return false;
        if (["30", "60", "90"].includes(f.scadenza) && !(d != null && d >= 0 && d <= Number(f.scadenza))) return false;
      }
      return true;
    });
  }, [services, f]);

  const agg = useMemo(() => {
    const byForn = {}, byTipo = {};
    let costo = 0, ricavo = 0;
    filtered.forEach((s) => {
      const c = num(s.prezzo_acquisto), r = num(s.prezzo_vendita);
      costo += c; ricavo += r;
      const fn = s.fornitore || "—";
      byForn[fn] = byForn[fn] || { fornitore: fn, count: 0, costo: 0, ricavo: 0 };
      byForn[fn].count++; byForn[fn].costo += c; byForn[fn].ricavo += r;
      const t = s.tipo || "—";
      byTipo[t] = byTipo[t] || { name: t, value: 0 };
      byTipo[t].value += r;
    });
    const fornRows = Object.values(byForn).map((x) => ({ ...x, margine: x.ricavo - x.costo, marginePct: x.ricavo ? ((x.ricavo - x.costo) / x.ricavo) * 100 : 0 })).sort((a, b) => b.ricavo - a.ricavo);
    const tipoRows = Object.values(byTipo).filter((x) => x.value > 0).sort((a, b) => b.value - a.value);
    return { costo, ricavo, margine: ricavo - costo, fornRows, tipoRows };
  }, [filtered]);

  const marginePct = agg.ricavo ? (agg.margine / agg.ricavo) * 100 : 0;

  const exportCsv = () => {
    const head = ["Tipo", "Nome", "Cliente", "Fornitore", "Scadenza", "Fatturazione", "Costo", "Ricavo", "Margine"];
    const rows = filtered.map((s) => [s.tipo || "", s.nome || "", displayCli(s), s.fornitore || "", (s.data_scadenza || "").slice(0, 10), s.fatturazione_ricorrente || "", num(s.prezzo_acquisto), num(s.prezzo_vendita), num(s.prezzo_vendita) - num(s.prezzo_acquisto)]);
    const csv = [head, ...rows].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(";")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `Report_Servizi_Vendita_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const loading = services === null;

  return (
    <InternalLayout>
      <div className="p-8 lg:p-12">
        <PageHeader
          title="Servizi in vendita"
          subtitle="Analisi costi e ricavi per software: domini, Sophos, Microsoft 365, Zyxel e altri"
          actions={<Button onClick={exportCsv} disabled={loading} className="rounded-sm gap-2" data-testid="svc-report-export"><DownloadSimple size={16} /> Esporta CSV</Button>}
        />

        {/* Filtri */}
        <Card className="rounded-sm border-border shadow-none p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
            <div className="lg:col-span-2">
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Ricerca</Label>
              <div className="relative mt-1">
                <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input value={f.q} onChange={(e) => set("q", e.target.value)} placeholder="Nome, cliente, fornitore, tipo…" className="pl-8 rounded-sm bg-white h-9" data-testid="svc-report-search" />
              </div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Software / Fornitore</Label>
              <div className="mt-1"><MultiSelect width="w-full" testid="svc-report-fornitore" placeholder="Tutti" value={f.fornitori} onChange={(v) => set("fornitori", v)} searchable options={opts.fornitori} /></div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Tipologia</Label>
              <div className="mt-1"><MultiSelect width="w-full" testid="svc-report-tipo" placeholder="Tutte" value={f.tipi} onChange={(v) => set("tipi", v)} searchable options={opts.tipi} /></div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Fatturazione ricorrente</Label>
              <div className="mt-1"><MultiSelect width="w-full" testid="svc-report-fatturazione" placeholder="Tutte" value={f.fatturazioni} onChange={(v) => set("fatturazioni", v)} options={opts.fatturazioni} /></div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Scadenza</Label>
              <Select value={f.scadenza} onValueChange={(v) => set("scadenza", v)}>
                <SelectTrigger className="mt-1 rounded-sm bg-white h-9" data-testid="svc-report-scadenza"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutte</SelectItem>
                  <SelectItem value="attivi">Attivi (non scaduti)</SelectItem>
                  <SelectItem value="30">In scadenza ≤ 30 gg</SelectItem>
                  <SelectItem value="60">In scadenza ≤ 60 gg</SelectItem>
                  <SelectItem value="90">In scadenza ≤ 90 gg</SelectItem>
                  <SelectItem value="scaduti">Scaduti</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Assegnazione cliente</Label>
              <Select value={f.assegnazione} onValueChange={(v) => set("assegnazione", v)}>
                <SelectTrigger className="mt-1 rounded-sm bg-white h-9" data-testid="svc-report-assegnazione"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="assegnati">Assegnati a cliente</SelectItem>
                  <SelectItem value="non_assegnati">Non assegnati</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">Margine</Label>
              <Select value={f.margine} onValueChange={(v) => set("margine", v)}>
                <SelectTrigger className="mt-1 rounded-sm bg-white h-9" data-testid="svc-report-margine"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti</SelectItem>
                  <SelectItem value="positivo">Positivo</SelectItem>
                  <SelectItem value="negativo">Negativo</SelectItem>
                  <SelectItem value="zero">A zero</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button variant="outline" onClick={() => setF(EMPTY_F)} className="rounded-sm gap-2 h-9 w-full" data-testid="svc-report-reset"><ArrowCounterClockwise size={15} /> Azzera filtri</Button>
            </div>
          </div>
        </Card>

        {loading ? (
          <div className="space-y-5">
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24 rounded-sm" />)}</div>
            <Skeleton className="h-[320px] rounded-sm" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
              <Kpi label="Servizi" value={filtered.length} icon={Stack} />
              <Kpi label="Costo totale" value={euro(agg.costo)} icon={CurrencyEur} accent="text-rose-600" />
              <Kpi label="Ricavo totale" value={euro(agg.ricavo)} icon={CurrencyEur} accent="text-primary" />
              <Kpi label="Margine totale" value={euro(agg.margine)} icon={TrendUp} accent="text-emerald-700" />
              <Kpi label="Margine %" value={pct(marginePct)} icon={TrendUp} accent={marginePct >= 0 ? "text-emerald-700" : "text-rose-600"} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
              <Card className="rounded-sm border-border shadow-none p-4 lg:col-span-2" data-testid="svc-report-chart-fornitore">
                <div className="text-sm font-semibold mb-3">Costi e ricavi per software</div>
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={agg.fornRows} margin={{ top: 8, right: 8, left: 0, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" vertical={false} />
                    <XAxis dataKey="fornitore" tick={{ fontSize: 11 }} angle={-25} textAnchor="end" interval={0} height={60} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip {...tipStyle} formatter={(v) => euro(v)} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="costo" name="Costo" fill="#dc2626" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="ricavo" name="Ricavo" fill="#00396a" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="margine" name="Margine" fill="#0b8457" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
              <Card className="rounded-sm border-border shadow-none p-4" data-testid="svc-report-chart-tipo">
                <div className="text-sm font-semibold mb-3">Ricavo per tipologia</div>
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie data={agg.tipoRows} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={110} label={(e) => e.name}>
                      {agg.tipoRows.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip {...tipStyle} formatter={(v) => euro(v)} />
                  </PieChart>
                </ResponsiveContainer>
              </Card>
            </div>

            {/* Riepilogo per software */}
            <Card className="rounded-sm border-border shadow-none mb-6">
              <div className="px-4 py-3 text-sm font-semibold border-b border-border">Riepilogo per software</div>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Software / Fornitore</TableHead><TableHead className="text-right">N. servizi</TableHead>
                  <TableHead className="text-right">Costo</TableHead><TableHead className="text-right">Ricavo</TableHead>
                  <TableHead className="text-right">Margine</TableHead><TableHead className="text-right">Margine %</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {agg.fornRows.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground" data-testid="svc-report-empty">Nessun servizio per i filtri selezionati.</TableCell></TableRow>
                    : agg.fornRows.map((r) => (
                      <TableRow key={r.fornitore} data-testid="svc-report-forn-row">
                        <TableCell className="font-medium">{r.fornitore}</TableCell>
                        <TableCell className="text-right tabular-nums">{r.count}</TableCell>
                        <TableCell className="text-right tabular-nums text-rose-600">{euro(r.costo)}</TableCell>
                        <TableCell className="text-right tabular-nums">{euro(r.ricavo)}</TableCell>
                        <TableCell className="text-right tabular-nums font-medium text-emerald-700">{euro(r.margine)}</TableCell>
                        <TableCell className="text-right tabular-nums">{pct(r.marginePct)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </Card>

            {/* Dettaglio servizi */}
            <Card className="rounded-sm border-border shadow-none">
              <div className="px-4 py-3 text-sm font-semibold border-b border-border flex items-center justify-between">
                <span>Dettaglio servizi</span>
                <span className="text-xs text-muted-foreground font-normal">{filtered.length} righe{filtered.length > 500 ? " · mostrate le prime 500" : ""}</span>
              </div>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Tipo</TableHead><TableHead>Nome</TableHead><TableHead>Cliente</TableHead><TableHead>Fornitore</TableHead>
                  <TableHead>Scadenza</TableHead><TableHead className="text-right">Costo</TableHead><TableHead className="text-right">Ricavo</TableHead><TableHead className="text-right">Margine</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {filtered.length === 0 ? <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Nessun servizio.</TableCell></TableRow>
                    : filtered.slice(0, 500).map((s) => {
                      const m = num(s.prezzo_vendita) - num(s.prezzo_acquisto);
                      return (
                        <TableRow key={s.id} data-testid="svc-report-detail-row">
                          <TableCell className="text-muted-foreground">{s.tipo || "—"}</TableCell>
                          <TableCell className="font-medium">{s.nome || "—"}</TableCell>
                          <TableCell>{displayCli(s)}</TableCell>
                          <TableCell className="text-muted-foreground">{s.fornitore || "—"}</TableCell>
                          <TableCell className="tabular-nums text-muted-foreground">{s.data_scadenza ? s.data_scadenza.slice(0, 10).split("-").reverse().join("/") : "—"}</TableCell>
                          <TableCell className="text-right tabular-nums text-rose-600">{euro(num(s.prezzo_acquisto))}</TableCell>
                          <TableCell className="text-right tabular-nums">{euro(num(s.prezzo_vendita))}</TableCell>
                          <TableCell className={`text-right tabular-nums font-medium ${m >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{euro(m)}</TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </Card>
          </>
        )}
      </div>
    </InternalLayout>
  );
}
